import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

export interface HealthSnapshot {
  /** Uptime in seconds. {@code null} when the gateway has never reported. */
  uptimeSeconds?: number | null;
  /**
   * Recent status entries — {@code timestamp} is ms-epoch or ISO string. The chip on top of
   * the timeline shows the most recent.
   */
  statusHistory?: Array<{ timestamp: string | number; status: string }>;
}

/**
 * Health monitoring card for a gateway. Renders uptime + a sparkline-ish status timeline
 * (color-coded squares per entry).
 *
 * <p>Lightweight version — no chart lib dependency. Once the design HTML is locked we can
 * replace the timeline with apexcharts. The shape is decoupled from the API, so the parent
 * is free to feed either the raw {@code /health} response or a derived snapshot.
 */
@Component({
  selector: 'lib-health-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3">
      <header class="flex items-center justify-between">
        <h3 class="text-sm font-medium text-gray-700">Health</h3>
        <span class="text-xs text-gray-500">{{ snapshot().statusHistory?.length ?? 0 }} entries</span>
      </header>

      <div class="rounded border border-gray-200 bg-gray-50 p-3">
        <div class="text-xs uppercase tracking-wide text-gray-500">Uptime</div>
        <div class="text-primary-500 mt-1 font-mono text-2xl">{{ uptimeLabel() }}</div>
      </div>

      <div class="rounded border border-gray-200 bg-gray-50 p-3">
        <div class="mb-2 text-xs uppercase tracking-wide text-gray-500">Status timeline</div>
        @if (snapshot().statusHistory?.length) {
          <div class="flex flex-wrap gap-1">
            @for (entry of snapshot().statusHistory ?? []; track entry.timestamp) {
              <span class="h-3 w-3 rounded-sm" [class]="dotForStatus(entry.status)" [title]="formatEntry(entry)"></span>
            }
          </div>
        } @else {
          <p class="text-xs text-gray-500">No status history yet.</p>
        }
      </div>
    </div>
  `,
})
export class HealthCardComponent {
  snapshot = input.required<HealthSnapshot>();

  protected readonly uptimeLabel = computed(() => {
    const s = this.snapshot().uptimeSeconds;
    if (s == null) return '—';
    return formatDuration(s);
  });

  protected dotForStatus(status: string): string {
    switch (status) {
      case 'ONLINE':
        return 'bg-aquamarine-500';
      case 'OFFLINE':
        return 'bg-gray-400';
      case 'CHARGING':
        return 'bg-warmYellow-500';
      default:
        return 'bg-gray-300';
    }
  }

  protected formatEntry(entry: { timestamp: string | number; status: string }): string {
    const d = typeof entry.timestamp === 'number' ? new Date(entry.timestamp) : new Date(entry.timestamp);
    if (isNaN(d.getTime())) return entry.status;
    return `${entry.status} @ ${d.toLocaleString()}`;
  }
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}
